import { HttpError } from '@pin/client-http-utils';
import { get, writable } from 'svelte/store';
import { loginSessionStore } from '../login/loginSessionStore';

const host = import.meta.env.VITE_PS_API_URL;

type FavoriteResponse = {
  _links: {
    deleteFavourite: {
      href: `/tv/userdata/${string}/favourites/${string}`;
    };
  };
};

export const checkFavourite = async (
  sourceMedium: 'tv' | 'radio',
  favouriteContentType: 'series' | 'programs' | 'podcast',
  contentId: string
) => {
  const loginSession = get(loginSessionStore);
  const accessToken = loginSession?.accessToken;
  const userId = loginSession?.user?.sub;

  if (!accessToken || !userId) {
    return undefined;
  }

  try {
    const response = await fetch(
      `${host}/${sourceMedium}/userdata/${userId}/favourites/${favouriteContentType}/${contentId}`,
      {
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
      }
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new HttpError(response);
    }
    const data: FavoriteResponse = await response.json();
    const favouriteId =
      data._links.deleteFavourite.href.split('favourites/')[1] ?? null;
    return favouriteId;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const addFavourite = async (
  sourceMedium: 'tv' | 'radio',
  favouriteContentType: 'series' | 'programs' | 'podcast',
  contentId: string
) => {
  const loginSession = get(loginSessionStore);
  const accessToken = loginSession?.accessToken;
  const userId = loginSession?.user?.sub;

  if (!accessToken || !userId) {
    return undefined;
  }

  try {
    const response = await fetch(
      `${host}/${sourceMedium}/userdata/${userId}/favourites/${favouriteContentType}/${contentId}`,
      {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({}),
      }
    );
    if (!response.ok) {
      throw new HttpError(response);
    }
    const data: FavoriteResponse = await response.json();
    const favouriteId =
      data._links.deleteFavourite.href.split('favourites/')[1] ?? null;
    return favouriteId;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const removeFavourite = async (
  sourceMedium: 'tv' | 'radio',
  favouriteId: string
) => {
  const loginSession = get(loginSessionStore);
  const accessToken = loginSession?.accessToken;
  const userId = loginSession?.user?.sub;
  if (accessToken && userId) {
    await fetch(
      `${host}/${sourceMedium}/userdata/${userId}/favourites/${favouriteId}`,
      {
        method: 'DELETE',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
      }
    );
  }
};

export const favouriteStore = (() => {
  const { update, subscribe } = writable<
    Record<string, string | null | undefined>
  >({});

  const storeFavouriteId = (contentId: string, favouriteId: string | null) => {
    update((prevValue) => ({
      ...prevValue,
      [contentId]: favouriteId,
    }));
  };

  const clearFavouriteId = (contentId: string) => {
    update((prevValue) => ({ ...prevValue, [contentId]: null }));
  };

  return { subscribe, storeFavouriteId, clearFavouriteId };
})();
